import { Button, Popover, Tooltip } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useFolderContentStats } from '../../../hooks/useFolderContentStats';
import type File from '../../../interfaces/File';
import { isLockedPasswordFolder } from '../../../lib/fileActions';
import { useAppStore } from '../../../store/store';
import { formatFileSize } from '../../../utils/utils';
import KeyValuePopoverGrid from '../../Common/KeyValuePopoverGrid';
import { Icon } from '../../Core/Icon';

interface FilesTableFileSizeCellProps {
  file: File;
}

const FilesTableFileSizeCell: React.FC<FilesTableFileSizeCellProps> = ({ file }) => {
  const { t } = useTranslation();
  const getFilePasswordSession = useAppStore((state) => state.getFilePasswordSession);
  const { canRefreshFolderStats, folderStats, isLoadingFolderStats, loadFolderStats } =
    useFolderContentStats(file);

  if (!file.isFolder) {
    return <span>{formatFileSize(file.fileSize)}</span>;
  }

  const isLockedFolder = isLockedPasswordFolder(file, getFilePasswordSession);

  const refreshTooltipTitle = isLockedFolder
    ? t('common.unlockFolderToViewSize')
    : canRefreshFolderStats
      ? t('common.recalculate')
      : t('common.recalculateCooldown');

  const refreshButton = (
    <Tooltip title={refreshTooltipTitle}>
      <Button
        type="link"
        size="small"
        className="text-blue-dark! hover:text-azure! h-[22px] p-0"
        aria-label={t('a11y.refreshFolderStats')}
        loading={isLoadingFolderStats}
        disabled={isLoadingFolderStats || !canRefreshFolderStats || isLockedFolder}
        onClick={(event) => {
          event.stopPropagation();
          void loadFolderStats();
        }}
      >
        <Icon name="RotateCwIcon" className="w-4" />
      </Button>
    </Tooltip>
  );

  const cellContent = (
    <span className="inline-flex items-center gap-1">
      {folderStats && <span>{formatFileSize(folderStats.totalSize)}</span>}
      {refreshButton}
    </span>
  );

  if (!folderStats) {
    return cellContent;
  }

  return (
    <Popover
      content={
        <KeyValuePopoverGrid
          rows={[
            { label: t('common.files'), value: folderStats.fileCount },
            { label: t('common.subfolders'), value: folderStats.folderCount },
          ]}
        />
      }
      trigger="hover"
    >
      {cellContent}
    </Popover>
  );
};

export default FilesTableFileSizeCell;
